import { Column, Row, TextTooltip } from '@/components';
import Layout from '@/components/Layout';
import { LINK, PATH } from '@/constants';
import { ShipmentPushStatus, ShipmentStatus } from '@/constants/shipment';
import { camelToRegularText, snakeToCamel } from '@/helpers';
import { CopyIcon } from '@/icons';
import { useGetShipmentDetailQuery, useGetUnfulfilledShipmentDetailQuery } from '@/redux/api/api.caller';
import { tokenSelector } from '@/redux/features/auth.slice';
import commonSlice from '@/redux/features/common.slice';
import shipmentSlice, { courierModalSelector } from '@/redux/features/shipment.slice';
import { BlockStack, Button, Icon, InlineStack, Link, Text, Tooltip, useBreakpoints } from '@shopify/polaris';
import { DeliveryIcon, EditIcon, ExternalSmallIcon } from '@shopify/polaris-icons';
import dayjs from 'dayjs';
import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { EShipmentType } from '../../config';
import { NOT_DETECTED_TRACKING_COMPANIES } from '../ShipmentTable/config';
import InfoCard from './components/InfoCard';
import ShipmentInfo from './components/ShipmentInfo';
import { ShipmentCarrierStyled } from './styled';
import useTrackingPageUrl from '@/hooks/useTrackingPageUrl';

const ShipmentDetail = () => {
  const location = useLocation();
  // Lấy state được truyền qua navigate
  const filter = location?.state?.filter;

  // Check if shipment is from unfulfilled tab
  // First try to get from location state, fallback to URL query params for page reload persistence
  const filterTypeFromState = filter
    ? (typeof filter === 'string' ? JSON.parse(filter) : filter)?.type
    : undefined;
  const searchParams = new URLSearchParams(location.search);
  const filterTypeFromUrl = searchParams.get('type') || undefined;
  const filterType = filterTypeFromState || filterTypeFromUrl;
  const isUnfulfilledShipment = filterType === EShipmentType.UNFULFILLED_SHIPMENT;

  const navigate = useNavigate();
  const { id } = useParams();
  const { shop } = useSelector(tokenSelector);
  const { proxyPath } = useTrackingPageUrl();

  // Construct filter object for API call
  // Use location state filter if available, otherwise construct minimal filter from URL params
  const apiFilter = filter || (filterType ? { type: filterType } : undefined);
  const shipmentQueryParams = {
    id: Number(id),
    filter: apiFilter ? JSON.stringify(apiFilter) : undefined,
  };

  const regularShipmentQuery = useGetShipmentDetailQuery(shipmentQueryParams, { skip: !id || isUnfulfilledShipment });
  const unfulfilledShipmentQuery = useGetUnfulfilledShipmentDetailQuery(shipmentQueryParams, {
    skip: !id || !isUnfulfilledShipment,
  });

  const { data, isLoading, error, isFetching } = isUnfulfilledShipment
    ? unfulfilledShipmentQuery
    : regularShipmentQuery;

  const shipment = isUnfulfilledShipment
    ? (data as any)?.data?.shipmentUnfulfill
    : (data as any)?.data?.shipment;
  const customer = shipment?.customer;
  const navigation = data?.data?.navigation;
  const { lgDown } = useBreakpoints();

  const courierModal = useSelector(courierModalSelector);
  const dispatch = useDispatch();

  // Redirect when isError is true
  useEffect(() => {
    if ((error as any)?.status === 404) {
      navigate(PATH.SHIPMENT, { replace: true });
    }
  }, [error, navigate]);

  // Show error toast when API fails
  useEffect(() => {
    if (error && !isLoading) {
      dispatch(
        commonSlice.actions.handleToast({
          content: (error as any)?.data?.message || 'Failed to fetch shipment details',
          isOpen: true,
          error: true,
        }),
      );
    }
  }, [error, isLoading, dispatch]);

  const renderTextWithCopyIcon = (copiedText?: string, component?: ReactNode) => (
    <div style={{ display: 'flex', gap: '0.25rem' }}>
      {component ? component : <TextTooltip str={copiedText || ''} />}
      {copiedText && <CopyIcon copiedText={copiedText} />}
    </div>
  );
  const handleClickEditCourierBtn = useCallback(
    (id: number, courier: string) =>
      dispatch(shipmentSlice.actions.handleChangeCourierModal({ ...courierModal, open: true, value: courier, id: [id] })),
    [dispatch, courierModal],
  );

  const orderInfoData = [
    {
      key: 'Order number',
      value: renderTextWithCopyIcon(
        shipment?.name,
        <Link url={LINK.ORDER_DETAIL(shop, String(shipment?.orderId))}>
          <TextTooltip str={shipment?.name || ''} />
        </Link>,
      ),
    },
    {
      key: 'Created at',
      value: dayjs(shipment?.createdAt).format('DD-MM-YYYY'),
    },
    {
      key: 'Order Note',
      value: shipment?.note,
    },
  ];

  const customerData = [
    {
      key: 'Name',
      value: (() => {
        if (customer?.first_name && customer?.last_name) return `${customer.first_name} ${customer.last_name}`;
        if (customer?.first_name) return customer.first_name;
        if (customer?.last_name) return customer.last_name;
        return '';
      })(),
    },
    {
      key: 'Email',
      value: renderTextWithCopyIcon(customer?.email),
    },
    {
      key: 'Phone',
      value: customer?.phone,
    },
    {
      key: 'Tags',
      value: customer?.tags,
    },
    {
      key: 'Email Marketing',
      value: camelToRegularText(snakeToCamel(customer?.email_marketing_consent?.state || '')),
    },
    {
      key: 'SMS Marketing',
      value: camelToRegularText(snakeToCamel(customer?.sms_marketing_consent?.state || '')),
    },
  ];

  const shippingAddressData = [
    {
      key: 'Address 1',
      value: shipment?.shippingAddress?.address1,
    },
    {
      key: 'Address 2',
      value: shipment?.shippingAddress?.address2,
    },
    {
      key: 'City',
      value: shipment?.shippingAddress?.city,
    },
    {
      key: 'Province/State',
      value: shipment?.shippingAddress?.province,
    },
    {
      key: 'ZIP/Postal code',
      value: shipment?.shippingAddress?.zip,
    },
    {
      key: 'Country',
      value: shipment?.shippingAddress?.country,
    },
  ];

  const shipmentInfoData = useMemo(() => {
    const renderCarrier = (type: 'firstMileCourier' | 'lastMileCourier') => {
      if (!shipment) return null;

      const carrierInfo = type === 'firstMileCourier' ? shipment.courier : shipment.lastMileCourier;
      const trackingNumber = type === 'firstMileCourier' ? shipment.trackingNumber : shipment.lastMileTrackingNumber;
      const trackingCompany = type === 'firstMileCourier' ? shipment.trackingCompany : shipment.lastMileCarrier;

      if (carrierInfo) {
        return (
          <ShipmentCarrierStyled>
            <InlineStack gap="300" blockAlign="center">
              <img src={carrierInfo?.logo} alt="carrier-logo" />
              <BlockStack>
                <Text as="p" variant="bodyMd" fontWeight="semibold">
                  {carrierInfo?.display_name}
                </Text>
                {renderTextWithCopyIcon(
                  trackingNumber,
                  <Link url={LINK.TRACKING_ORDER_NOW(shop, trackingNumber || '', proxyPath)}>
                    <TextTooltip str={trackingNumber || ''} trimLength={25} />
                  </Link>,
                )}
              </BlockStack>
            </InlineStack>
            <Button icon={ExternalSmallIcon} variant="plain" url={carrierInfo?.website} />
          </ShipmentCarrierStyled>
        );
      }

      const isTrackingCompanyNotDetected = NOT_DETECTED_TRACKING_COMPANIES.some(
        (company) => shipment.trackingCompany && company.toLowerCase() === shipment.trackingCompany.toLowerCase(),
      );

      const shouldChangeCarrier =
        (shipment.trackingSummary === ShipmentStatus.CourierNotSupported ||
          shipment.trackingSummary === ShipmentStatus.NotFound ||
          shipment.pushStatus === ShipmentPushStatus.NOT_DETECT ||
          isTrackingCompanyNotDetected);

      return (
        <InlineStack gap="400" blockAlign="center" align="space-between">
          <ShipmentCarrierStyled>
            <div className="default-carrier-img">
              <Icon source={DeliveryIcon} tone="base" />
            </div>
            <BlockStack>
              <Text as="p" variant="bodyMd" fontWeight="semibold">
                {trackingCompany}
              </Text>
              {renderTextWithCopyIcon(trackingNumber, <TextTooltip str={trackingNumber || ''} trimLength={25} />)}
            </BlockStack>
          </ShipmentCarrierStyled>
          {shouldChangeCarrier && trackingCompany && (
            <Tooltip
              content={
                shipment.pushStatus === ShipmentPushStatus.PROCESSING
                  ? 'This shipment is being processed. Change carrier is not available.'
                  : 'Change carrier'
              }
            >
              <Button
                icon={EditIcon}
                variant="plain"
                disabled={shipment.pushStatus === ShipmentPushStatus.PROCESSING}
                onClick={() => {
                  handleClickEditCourierBtn(shipment.id, trackingCompany);
                }}
              >
                Edit
              </Button>
            </Tooltip>
          )}
        </InlineStack>
      );
    };

    const defaultCarrier = {
      key: 'Carrier',
      direction: 'vertical' as const,
      value: renderCarrier('firstMileCourier'),
    };

    if (!shipment?.lastMileTrackingNumber && !shipment?.lastMileCarrier) {
      return [defaultCarrier];
    }
    return [
      defaultCarrier,
      {
        key: 'Last-mile carrier',
        direction: 'vertical' as const,
        value: renderCarrier('lastMileCourier'),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipment, handleClickEditCourierBtn, shop]);

  const subtitleContent = useMemo(() => {
    if (!shipment) return '';
    if (isUnfulfilledShipment) return `Order number`;
    if (shipment.lastMileTrackingNumber) return `Last-mile tracking number: ${shipment.lastMileTrackingNumber}`;
    if (shipment.trackingNumber) return `Tracking number: ${shipment.trackingNumber}`;
    if (shipment.orderId) return 'Order number';
    return '';
  }, [shipment, isUnfulfilledShipment]);

  return (
    <Layout
      backAction={{ url: PATH.SHIPMENT }}
      title={shipment?.trackingNumber || (shipment?.orderId && shipment.name?.replace('#', '')) || 'Shipment details'}
      subtitle={subtitleContent}
      pagination={{
        hasPrevious: !isLoading && !isFetching && Boolean(navigation?.canNavigatePrevious),
        hasNext: !isLoading && !isFetching && Boolean(navigation?.canNavigateNext),
        onPrevious: () => {
          if (navigation?.previousId) {
            // Include type in URL query params to persist on page reload
            const typeParam = filterType ? `?type=${filterType}` : '';
            navigate(`${PATH.SHIPMENT}/${navigation.previousId}${typeParam}`, { state: { filter } });
          }
        },
        onNext: () => {
          if (navigation?.nextId) {
            // Include type in URL query params to persist on page reload
            const typeParam = filterType ? `?type=${filterType}` : '';
            navigate(`${PATH.SHIPMENT}/${navigation.nextId}${typeParam}`, { state: { filter } });
          }
        },
      }}
    >
      <Row column={lgDown ? 2 : 3}>
        <Column span={2}>
          <ShipmentInfo />
        </Column>
        <Column span={lgDown ? 2 : 1}>
          <BlockStack gap="400">
            {!isUnfulfilledShipment && shipment?.trackingNumber && (
              <InfoCard title="Shipment information" data={shipmentInfoData} loading={isLoading} />
            )}
            <InfoCard title="Order information" data={orderInfoData} loading={isLoading} />
            <InfoCard title="Customer information" data={customerData} loading={isLoading} />
            <InfoCard title="Shipping address" data={shippingAddressData} loading={isLoading} />
          </BlockStack>
        </Column>
      </Row>
    </Layout>
  );
};

export default ShipmentDetail;
